/*
,------.                                    ,--.          ,--.      ,--------.    ,------.            ,--.           
|  .-.  \,--. ,--.,--,--,  ,--,--.,--,--,--.`--' ,---.    |  | ,---.'--.  .--'    |  .-.  \  ,--,--.,-'  '-. ,--,--. 
|  |  \  :\  '  / |      \' ,-.  ||        |,--.| .--'    |  || .-. |  |  |       |  |  \  :' ,-.  |'-.  .-'' ,-.  | 
|  '--'  / \   '  |  ||  |\ '-'  ||  |  |  ||  |\ `--.    |  |' '-' '  |  |       |  '--'  /\ '-'  |  |  |  \ '-'  | 
`-------'.-'  /   `--''--' `--`--'`--`--`--'`--' `---'    `--' `---'   `--'       `-------'  `--`--'  `--'   `--`--' 
         `---'                                                                                                      
*/
import { IdName } from 'src/service/asset';
import { formatDate, VisionApplication } from '../../application';
import { VisionWindow } from '../../window/window';
import { VisionRockingTab } from './rocking';
import { RockingProgramDTO, RockingProgramSegmentDTO, RockingSegmentDTO, VisionRockingService } from './rocking.service';
import { Measurement } from '../../components/measurement';

export class VisionRockProgramWindow2 extends VisionWindow {
    private layout!: dhtmlXLayoutObject;
    private dragDropLayout!: dhtmlXLayoutObject;
    private assignedSegments!: dhtmlXGridObject;

    private sideBar!: dhtmlXSideBar;
    private programGrid!: dhtmlXGridObject;
    private segmentGrid!: dhtmlXGridObject;
    private toolbar!: dhtmlXToolbarObject;

    private form!: dhtmlXForm;
    private contextMenu?: dhtmlXMenuObject;

    private service: VisionRockingService;
    private segmentCache: Map<string, RockingSegmentDTO>;
    private programCache: Map<string, RockingProgramDTO>;
    private primaryId!: string;

    private dirty = false;
    private owner: VisionRockingTab;

    constructor(parent: VisionRockingTab) {
        super(VisionApplication.getInstance().windowManager, {
            id: 'rock_segment_window',
            title: 'Edit Rocking Segment',
            x: 30,
            y: 30,
            width: 1600,
            height: 600,
        });
        this.window.setText('Manage Rocking Programs');
        this.modal = true;
        this.owner = parent;

        this.service = VisionApplication.getInstance().rockingService;

        this.segmentCache = new Map<string, RockingSegmentDTO>();
        this.programCache = new Map<string, RockingProgramDTO>();

        this.setupGUI();
        this.setupEvents();
    }

    private setupGUI() {
        this.layout = this.window.attachLayout('2U');
        this.layout.cells('a').setText('Programs');
        this.layout.cells('a').setWidth(200);
        this.layout.cells('b').setText('Configuration');

        this.toolbar = this.layout.cells('b').attachToolbar();
        this.toolbar.addButton('new', 0, 'New', 'icons/sign.png', '');
        this.toolbar.addButton('save', 1, 'Save', 'css/imgs/save.gif', '');
        this.toolbar.addButton('copy', 2, 'Copy', 'css/imgs/copy.png', '');
        this.toolbar.addSeparator('sep1', 4);
        this.toolbar.addButton('delete', 5, 'Delete', 'icons/clear.png', '');

        this.sideBar = this.layout.cells('b').attachSidebar();
        this.sideBar.addItem({
            id: 'program_info',
            text: 'Details',
            icon: 'icons/information.png',
        });

        this.sideBar.addItem({
            id: 'program_segments',
            text: 'Segments',
            icon: 'icons/meta.png',
        });
        this.sideBar.cells('program_info').setActive();

        this.programGrid = this.layout.cells('a').attachGrid();
        this.programGrid.setHeader('Name,Start,End', '', ['text-align:center;', 'text-align:center', 'text-align:center']);
        this.programGrid.attachHeader(['#text_filter', '', ''], []);
        this.programGrid.setColAlign('left,center,center');
        this.programGrid.setColTypes('ro,ro,ro');
        this.programGrid.init();

        this.setupDetails();
        this.layout.cells('a').hideHeader();
        this.layout.cells('b').hideHeader();
        this.setupSegments();
        this.populatePrograms(true);
    }

    private setupDetails(): void {
        const form_data = [
            {
                type: 'settings',
                position: 'label-left',
                labelWidth: 90,
                inputWidth: 130,
            },
            {
                name: 'id',
                type: 'input',
                label: 'ID',
                value: '',
                inputWidth: '250',
                offsetLeft: '25',
                offsetTop: '25',
                disabled: true,
            },
            {
                name: 'name',
                type: 'input',
                label: 'Program Name',
                value: '',
                inputWidth: '250',
                offsetLeft: '25',
                required: false,
            },
            {
                name: 'description',
                type: 'input',
                label: 'Description',
                value: '',
                rows: '3',
                inputWidth: '250',
                offsetLeft: '25',
            },
            {
                type: 'calendar',
                name: 'program_start',
                label: 'Start',
                enableTime: false,
                dateFormat: '%m-%d-%Y',
                labelWidth: '120',
                position: 'label-left',
                offsetLeft: '25',
            },
            {
                type: 'calendar',
                name: 'program_end',
                label: 'End',
                enableTime: false,
                dateFormat: '%m-%d-%Y',
                labelWidth: '120',
                position: 'label-left',
                offsetLeft: '25',
            },
            {
                name: 'default_tonnage',
                type: 'input',
                label: 'Default Segment Tonnage',
                value: '',
                inputWidth: '250',
                offsetLeft: '25',
                disabled: false,
            },
        ];
        this.form = this.sideBar.cells('program_info').attachForm({});
        this.form.loadStruct(form_data);
    }

    private setupSegments(): void {
        this.dragDropLayout = this.sideBar.cells('program_segments').attachLayout('2U');

        this.dragDropLayout.cells('a').setText('Zones & Routes');
        this.dragDropLayout.cells('a').hideArrow();
        this.dragDropLayout.cells('a').hideHeader();

        this.dragDropLayout.cells('b').setText('Segments');
        this.dragDropLayout.cells('b').hideHeader();
        this.dragDropLayout.cells('b').hideArrow();
        this.segmentGrid = this.dragDropLayout.cells('b').attachGrid();
        this.segmentGrid.setHeader('Name,Ton', '', ['text-align:center;', 'text-align:center']);
        this.segmentGrid.attachHeader(['#text_filter', ''], []);
        this.segmentGrid.setColAlign('left,center');
        this.segmentGrid.setColTypes('ro,ro');
        this.segmentGrid.setColumnIds('name,default');
        this.segmentGrid.setColSorting('int');
        this.segmentGrid.enableDragAndDrop(true);
        this.segmentGrid.enableMultiselect(true);

        this.segmentGrid.attachEvent('onRowSelect', (id) => {
            console.log('Selected Row ID: ' + id);
        });

        this.segmentGrid.treeToGridElement = (_treeObj, treeNodeId, _gridRowId) => {
            const id = treeNodeId as string;
            return [this.segmentCache.get(id)?.id.name, this.segmentCache.get(id)?.requiredKg];
        };

        this.segmentGrid.init();

        this.primaryId = crypto.randomUUID();
        const newID = this.primaryId;
        this.assignedSegments = this.dragDropLayout.cells('a').attachGrid();
        this.assignedSegments.setHeader('Name,Default,Configured', '', ['text-align:center;', 'text-align:center', 'text-align:center']);
        this.assignedSegments.setColAlign('left,center,center');
        this.assignedSegments.setColTypes('tree,ro,ed');
        this.assignedSegments.setColumnIds('name,default,configured');
        this.assignedSegments.setImagesPath('css/imgs/rocking_tree/');
        this.assignedSegments.enableDragAndDrop(true);
        this.assignedSegments.enableMultiselect(true);
        this.assignedSegments.init();

        this.assignedSegments.addRow(newID, 'Base Level,-,-', 0, '', 'folder.gif', false);
        this.assignedSegments.setUserData(newID, 'type', 'branch');

        this.segmentGrid.attachEvent('onDrag', (_sourceId, _tId, _sObj, _tObj, _sCol, _tCol) => {
            return this.availableSegmentDrag(_sourceId, _tId, _sObj, _tObj, _sCol, _tCol);
        });
        this.assignedSegments.attachEvent('onDrag', (_sourceId, _tId, _sObj, _tObj, _sCol, _tCol) => {
            return this.assignedSegmentDrag(_sourceId, _tId, _sObj, _tObj, _sCol, _tCol);
        });

        this.contextMenu = new dhtmlXMenuObject();
        this.contextMenu.setIconsPath('css/imgs/');
        this.contextMenu.renderAsContextMenu();

        this.contextMenu.loadStruct('<menu id="0"><item text="Add Level" id="add_level"/></menu>');
        this.assignedSegments.enableContextMenu(this.contextMenu);

        this.assignedSegments.attachEvent('onBeforeContextMenu', (id) => {
            return this.assignedSegments.getUserData(id, 'type') == 'branch';
        });

        this.contextMenu.attachEvent('onClick', (menuItemId, _type) => {
            if (menuItemId == 'add_level') {
                const newId = crypto.randomUUID();
                const contextId = this.assignedSegments.contextID.split('_')[0];
                this.assignedSegments.addRow(newId, 'New Level,-,-', 0, contextId, 'folder.gif', false);
                this.assignedSegments.setUserData(newId, 'type', 'branch');
                this.assignedSegments.openItem(contextId);
            }
        });
        this.cacheSegments();
    }

    private cacheSegments(): void {
        const request = this.service.cachedFetchData();
        request.then((data) => {
            data.forEach((segment) => {
                const required = segment.requiredKg == undefined ? '-' : Measurement.kg2ImperialTons(Number(segment.requiredKg));
                this.segmentGrid.addRow(segment.id.id, [segment.id.name, required], 0, '', '', false);
                this.segmentGrid.setUserData(segment.id.id, 'type', 'leaf');
                this.segmentCache.set(segment.id.id, segment);
            });
        });
    }

    private setupEvents(): void {
        this.toolbar.attachEvent('onClick', (id) => {
            this.handleToolbar(id);
        });

        this.programGrid.attachEvent('onBeforeSelect', (oldId, newId) => {
            if (!this.dirty) {
                return true;
            }
            const result = window.confirm('You have unsaved changes. Are you sure you want to select a new program?');
            if (result) {
                this.dirty = false;
            }
            return result;
        });

        this.programGrid.attachEvent('onRowSelect', (rid) => {
            this.selectProgram(rid);
        });

        this.assignedSegments.attachEvent('onEditCell', (stage, rId, cInd, nValue, oValue) => {
            this.dirty = true;
            return true;
        });
    }

    private selectProgram(pid: string): void {
        const program = this.programCache.get(pid);
        if (program) {
            this.form.setItemValue('name', program.id.name);
            this.showProgram(program);
        }
    }

    private clearAssignedSegments(): void {
        this.assignedSegments.forEachRow((id) => {
            const segId = this.assignedSegments.getUserData(id, 'segmentId');
            if (segId) {
                this.segmentGrid.setRowColor(segId, '');
            }
        });
    }

    /** Populates the window with a selected program
     *
     * @param program The program to use.
     */
    private showProgram(program: RockingProgramDTO): void {
        this.clearAssignedSegments();
        this.clearForm();

        this.form.setItemValue('id', program.id.id);
        this.form.setItemValue('program_start', new Date(program.start));
        this.form.setItemValue('program_end', new Date(program.end));
        this.form.setItemValue('name', program.id.name);
        if (program.defaultKg != undefined) {
            this.form.setItemValue('default_tonnage', Measurement.kg2ImperialTons(program.defaultKg));
        }

        this.assignedSegments.clearAll(false);

        program.folders?.forEach((groupData) => {
            const rowData = [groupData.name, '-', '-'];
            this.assignedSegments.addRow(groupData.id, rowData, 0, groupData.parentId ?? 0, 'folder.gif', false);
            this.assignedSegments.setUserData(groupData.id, 'type', 'branch');
        });

        program.segments.forEach((segment) => {
            const segData = this.segmentCache.get(segment.segmentId ?? '');
            let defaultProgramKg = '-';

            if (segData?.requiredKg != undefined) {
                defaultProgramKg = segData.requiredKg.toString();
            }
            if (program.defaultKg != undefined) {
                defaultProgramKg = program.defaultKg.toString();
            }

            let requiredKg = '-';
            if (segment.requiredKg != undefined) {
                requiredKg = segment.requiredKg.toString();
            }

            const segmentName = segData?.id.name ?? 'Segment Name';
            const rowData = [segmentName, Measurement.kg2ImperialTons(Number(defaultProgramKg)), Measurement.kg2ImperialTons(Number(requiredKg))];
            this.assignedSegments.addRow(segment.id, rowData, 0, segment.parentId, '', false);
            this.assignedSegments.setUserData(segment.id, 'type', 'leaf');
            this.assignedSegments.setUserData(segment.id, 'segmentId', segment.segmentId ?? '');

            // Mark as assigned already.
            if (segment.segmentId) {
                this.segmentGrid.setRowColor(segment.segmentId, '#B2BEB5');
            }
        });

        program.folders?.forEach((groupData) => {
            this.assignedSegments.openItem(groupData.id);
        });
    }

    private handleToolbar(id: string): void {
        switch (id) {
            case 'save': {
                this.saveProgram();
                break;
            }
            case 'new': {
                this.clearForm();
                break;
            }

            case 'copy': {
                this.clearForm();
                this.copy();
                break;
            }

            case 'delete': {
                this.deleteProgram();
                break;
            }
        }
    }

    /** Creates a copy of the currently selected program.
     *
     */
    private copy(): void {
        const programId = this.programGrid.getSelectedRowId().toString();

        //this.selectProgram(programId);
        const program = this.programCache.get(programId);

        if (program != undefined) {
            const cloned = this.cloneProgramTree(program);
            cloned.id = {} as IdName;
            cloned.id.id = '';

            this.showProgram(cloned);
        }

        if (program != undefined) {
            this.form.setItemValue('id', '');
            this.form.setItemValue('name', 'Copy of ' + program.id.name);
            this.form.setItemValue('description', program.description);
            this.form.setItemValue('program_start', '');
            this.form.setItemValue('program_end', '');
        }
    }

    /** Create a copy of the program segment tree.
     *  Does this by going through the tree and creating new UUIDs for them.
     * @param program The program that has the segment tree that we want to copy.
     * @returns A new program that has the copy of the tree.
     */
    private cloneProgramTree(program: RockingProgramDTO): RockingProgramDTO {
        const cloned = {} as RockingProgramDTO;

        const folders = [...program.folders];
        const segments = [...program.segments];
        const mapped = new Map<string, string>();

        // Set all the groups to have a new ID and create a map of the old ids vs the new ids
        folders.forEach((groupData) => {
            const newId = crypto.randomUUID();
            mapped.set(groupData.id, newId);
            groupData.id = newId;
        });

        // Make sure all the folders are setup with a new parent ids
        folders.forEach((groupData) => {
            if (groupData.parentId != null) {
                const newParent = mapped.get(groupData.parentId);
                if (newParent) {
                    groupData.parentId = newParent;
                }
            }
        });

        // For all the segments just create new id's for them and assign them to their new parent id
        segments.forEach((groupData) => {
            const newId = crypto.randomUUID();
            groupData.id = newId;
            const newParent = mapped.get(groupData.parentId);
            if (newParent) {
                groupData.parentId = newParent;
            }
        });

        cloned.folders = folders;
        cloned.segments = segments;

        return cloned;
    }

    /** Clears out the form to allow for new program to be created.
     *
     */
    private clearForm(): void {
        this.form.setItemValue('id', '');
        this.form.setItemValue('name', '');
        this.form.setItemValue('description', '');
        this.form.setItemValue('program_start', '');
        this.form.setItemValue('program_end', '');
        this.form.setItemValue('default_tonnage', '');

        this.primaryId = crypto.randomUUID();
        this.assignedSegments.clearAll(false);
        const newID = this.primaryId;
        this.assignedSegments.addRow(newID, 'Base Level,-,-', 0, '', 'folder.gif', false);
        this.assignedSegments.setUserData(newID, 'type', 'branch');

        // Clear the assigned segments and reset the color of the available segments.
        this.clearAssignedSegments();

        //this.sideBar.cells('program_info').setActive();

        this.assignedSegments.clearAll(false);
    }

    /** Populate the grid of available programs.
     *  Makes a request to the server to get the list of programs and builds up the prgram cache.
     * @param reload
     */
    private populatePrograms(reload: boolean): void {
        this.programGrid.clearAll(false);
        this.programCache.clear();

        const request = this.service.getPrograms(reload);
        request.then((data) => {
            data.forEach((program) => {
                this.programGrid.addRow(program.id.id, [program.id.name, formatDate(Number(program.start)), formatDate(Number(program.end))], 0, '', '', false);
                this.programCache.set(program.id.id, program);
            });
        });
    }

    /** Deletes the currently selected program.
     *  Sends a request to the server to delete the currenntly selected program.
     *  Will also remove it from the cache and grid list.
     */
    private deleteProgram(): void {
        const programid = this.form.getItemValue('id');
        const programName = this.form.getItemValue('name');

        dhtmlx.confirm({
            type: 'confirm-warning',
            title: 'Confirm Delete',
            text: 'Delete Program: ' + programName,
            callback: (result: boolean) => {
                if (result == true) {
                    this.service.deleteProgram(programid).then((data) => {
                        if (data.status == 200) {
                            dhtmlx.message({
                                title: 'Complete',
                                text: 'Program Deleted',
                                type: 'alert-success',
                            });
                            this.clearForm();
                            this.programGrid.deleteRow(programid);
                            this.programCache.delete(programid);
                            this.owner.reload();
                        } else {
                            dhtmlx.message({
                                title: 'Error',
                                text: 'Failed to delete program',
                                type: 'alert-error',
                            });
                        }
                    });
                }
            },
        });
    }

    /** Save the program
     *
     */
    private saveProgram(): void {
        if (this.verifyForm() == false) {
            return;
        }

        const programid = this.form.getItemValue('id');
        const name = this.form.getItemValue('name');
        const description = this.form.getItemValue('description');
        const start = this.form.getItemValue('program_start').getTime();
        const end = this.form.getItemValue('program_end').getTime();
        const defaultTonnage = this.form.getItemValue('default_tonnage');

        const data = {} as RockingProgramDTO;
        data.id = { id: programid, name: name };
        data.description = description;
        data.start = start;
        data.end = end;
        if (defaultTonnage != undefined && defaultTonnage != '') {
            data.defaultKg = Measurement.imperialTons2Kg(defaultTonnage);
        }

        const folders = new Array<RockingProgramSegmentDTO>();
        const segments = new Array<RockingProgramSegmentDTO>();

        this.assignedSegments.forEachRow((id) => {
            const type = this.assignedSegments.getUserData(id, 'type');
            const currentParent = this.assignedSegments.getParentId(id);
            if (type == 'branch') {
                folders.push({
                    id: id,
                    name: this.assignedSegments.cells(id, 0).getValue(),
                    parentId: currentParent.toString(),
                    requiredKg: 0,
                });
            } else if (type == 'leaf') {
                let required = 0;
                const currentParent = this.assignedSegments.getParentId(id);
                const defaultKg = this.assignedSegments.cells(id, 1).getValue().toString();

                if (this.assignedSegments.cells(id, 2).getValue() != '-') {
                    const configuredKg = Measurement.imperialTons2Kg(Number(this.assignedSegments.cells(id, 2).getValue()));
                    required = configuredKg;
                } else {
                    required = Number(Measurement.imperialTons2Kg(Number(defaultKg)));
                }
                const segmentId = this.assignedSegments.getUserData(id, 'segmentId');
                segments.push({
                    id: id,
                    segmentId: segmentId,
                    parentId: currentParent.toString(),
                    requiredKg: required,
                });
            }
        });

        data.folders = folders;
        data.segments = segments;

        const saveRequest = this.service.saveProgram(data);
        saveRequest.then((data) => {
            dhtmlx.message({
                title: 'Saved',
                text: 'Program ' + data.parsedBody?.id.name + ' was saved',
                type: 'alert-success',
            });
            this.populatePrograms(true);
            this.form.setItemValue('id', data.parsedBody?.id.id);
        });
    }

    private updateRow(data: RockingProgramDTO | undefined) {
        if (data == undefined) {
            return;
        } else {
            this.programGrid.deleteRow(data.id.id);
            this.addRow(data);
        }
    }

    private addRow(data: RockingProgramDTO | undefined) {
        if (data == undefined) {
            return;
        }
        const start = new Date(data.start);
        const end = new Date(data.end);
        this.programGrid.addRow(data.id.id, [data.id.name, start, end], 0, '', '', false);
    }

    private verifyForm(): boolean {
        const name = this.form.getItemValue('name');
        const start = this.form.getItemValue('program_start');
        const end = this.form.getItemValue('program_end');

        if (name == undefined || name == '') {
            dhtmlx.alert({
                title: 'Program Error',
                text: 'Program requires a name.',
                type: 'alert-error',
            });
            return false;
        }

        if (start == undefined || start == '') {
            dhtmlx.alert({
                title: 'Program Error',
                text: 'Start time required',
                type: 'alert-error',
            });
            return false;
        }
        if (end == undefined || end == '') {
            dhtmlx.alert({
                title: 'Program Error',
                text: 'End time required',
                type: 'alert-error',
            });
            return false;
        }

        return true;
    }

    /** Handles dragging segments from the assigned segments grid back to the segment grid to remove them from the group, as well as deleting segments by dragging them to the trash icon in the context menu.
     *
     * @param _sourceId
     * @param _tId
     * @param _sObj
     * @param _tObj
     * @param _sCol
     * @param _tCol
     * @returns
     */
    private availableSegmentDrag(_sourceId: string, _tId: string, _sObj: dhtmlXGridObject, _tObj: dhtmlXGridObject, _sCol: number, _tCol: number): boolean {
        this.dirty = true;

        if (_sObj === this.assignedSegments) {
            // Can't remove the root node
            if (_sObj.getParentId(_sourceId) == 0) {
                return false;
            }
            const ids = _sourceId.split(',');
            for (const id of ids) {
                if (this.assignedSegments.getUserData(id, 'type') === 'branch') {
                    const allSubItems = this.assignedSegments.getAllSubItems(id).split(',');

                    for (const subItemId of allSubItems) {
                        if (this.assignedSegments.getUserData(subItemId, 'type') === 'leaf') {
                            const segmentId = this.assignedSegments.getUserData(subItemId, 'segmentId');
                            this.segmentGrid.setRowColor(segmentId, '');
                        }
                    }
                } else {
                    const segmentId = this.assignedSegments.getUserData(id, 'segmentId');
                    this.segmentGrid.setRowColor(segmentId, '');
                }

                this.assignedSegments.deleteRow(id);
            }
        }
        return false;
    }

    /** Handles dragging segments from the segment grid to the assigned segments grid, as well as reordering within the assigned segments grid.
     *
     * @param _sourceId
     * @param _tId
     * @param _sObj
     * @param _tObj
     * @param _sCol
     * @param _tCol
     * @returns
     */
    private assignedSegmentDrag(_sourceId: string, _tId: string, _sObj: dhtmlXGridObject, _tObj: dhtmlXGridObject, _sCol: number, _tCol: number): boolean {
        this.dirty = true;

        // Drag Segments => Assigned
        if (_sObj === this.segmentGrid) {
            let parentId = _tId;
            const targetRowType = this.assignedSegments.getUserData(_tId, 'type');
            // If the target is a leaf, add items as siblings
            if (targetRowType === 'leaf') {
                parentId = _tObj.getParentId(_tId);
            }

            // Can be multiple segments so split the ids and loop through them
            const ids = _sourceId.split(',');
            for (const id of ids) {
                // Make sure segment is not already in the group.
                if (this.hasUserData(this.assignedSegments, 'segmentId', id) == false) {
                    const rowData = this.segmentGrid.getRowData(id);
                    // Set the color of the segment to indicate it's been added to the group
                    this.segmentGrid.setRowColor(id, '#B2BEB5');

                    if (this.form.getItemValue('default_tonnage') != undefined && this.form.getItemValue('default_tonnage') != '') {
                        rowData.default = Measurement.kg2ImperialTons(Number(Measurement.imperialTons2Kg(this.form.getItemValue('default_tonnage'))));
                    }

                    // Assign a unique id for the new row in the assigned segments grid, and set user data to identify which segment it corresponds to
                    const uniqueId = crypto.randomUUID();
                    this.assignedSegments.addRow(uniqueId, [rowData.name, rowData.default, '-'], 0, parentId, 'blank.gif', false);
                    this.assignedSegments.setUserData(uniqueId, 'type', 'leaf');
                    this.assignedSegments.setUserData(uniqueId, 'segmentId', id);
                    this.assignedSegments.openItem(parentId);
                } else {
                    // Warn that the segment is already in the group and prevent adding it again
                    dhtmlx.message({
                        title: 'error',
                        text: 'Segment ' + this.segmentGrid.cells(id, this.segmentGrid.getColIndexById('name')).getValue() + ' is already in the group.',
                        type: 'error',
                    });
                }
            }
            this.segmentGrid.clearSelection();
            //this.assignedSegments.sortRows(0, 'str', 'asc'); // Not sure if needed or not.
            return false;
        }
        // Reordering within Assigned Segments
        else if (_tObj === this.assignedSegments) {
            const targetRowType = this.assignedSegments.getUserData(_tId, 'type');

            if (targetRowType === 'leaf') {
                console.log('Target ID: ' + _tObj.getParentId(_tId));
                this.assignedSegments.moveRowTo(_sourceId, _tId, 'move', 'sibling');
                return false;
            } else if (targetRowType === 'branch') {
                this.assignedSegments.moveRowTo(_sourceId, _tId, 'move', 'child');
                return false;
            }
        }
        return false;
    }

    private hasUserData(grid: dhtmlXGridObject, userDataKey: string, userDataValue: string): boolean {
        let hasUserData = false;
        grid.forEachRow((rowId) => {
            const data = grid.getUserData(rowId, userDataKey);
            if (data === userDataValue) {
                hasUserData = true;
            }
        });
        return hasUserData;
    }
}
